import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  TextField,
  Autocomplete,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material';
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { SirketLookup } from './SirketLookup';
import { bankServiceRunService } from '../services/bankServiceRunService';
import { bankaService } from '../services/bankaService';
import { serverService } from '../services/serverService';
import { agentService } from '../services/agentService';
import type { Server } from '../types/server';
import type { Banka } from '../types/banka';
import type { SirketHesapDto } from '../types/bankServiceRun';

interface RequestParams {
  bankCode?: number;
  serviceVkn?: string;
  uid?: string;
  logLevel?: number;
  beginDate?: string;
  endDate?: string;
  dataOnly?: boolean;
  inspect?: boolean;
  skipJobLog?: boolean;
  nextRunSkip?: boolean;
  manuelRun?: boolean;
  runWithoutDate?: boolean;
  requestInclude?: boolean;
}

const defaultFormState = {
  serverId: '',
  serviceVkn: '',
  bankCode: '',
  uid: '',
  beginDate: '',
  endDate: '',
  dataOnly: false,
  inspect: true,
  skipJobLog: true,
  nextRunSkip: true,
  manuelRun: false,
  runWithoutDate: false,
  requestResponseDownload: false,
  requestInclude: true,
};

const getDateTimeLocalNow = (): string => {
  const n = new Date();
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, '0');
  const d = String(n.getDate()).padStart(2, '0');
  const h = String(n.getHours()).padStart(2, '0');
  const min = String(n.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
};

const getDateTimeLocalOneDayLater = (): string => {
  const n = new Date();
  n.setDate(n.getDate() + 1);
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, '0');
  const d = String(n.getDate()).padStart(2, '0');
  const h = String(n.getHours()).padStart(2, '0');
  const min = String(n.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
};

const toCliDate = (dateStr: string | undefined): string => {
  if (!dateStr || !dateStr.trim()) return '';
  const s = dateStr.trim();
  const [datePart, timePart] = s.split('T');
  const [y, m, d] = datePart.split('-');
  if (!y || !m || !d) return s;
  const t = timePart && /^\d{1,2}:\d{2}/.test(timePart) ? timePart.slice(0, 5) : '00:00';
  return `${y}/${m}/${d}T${t}`;
};

const buildRequestCommandLine = (requestParametersJson: string | undefined): string => {
  if (!requestParametersJson || !requestParametersJson.trim()) return '';
  try {
    const p = JSON.parse(requestParametersJson) as RequestParams;
    const logLevel = p.logLevel ?? 0;
    const parts: string[] = ['dotnet estatement_service.dll', '-r', `-l${logLevel}`];
    if (p.serviceVkn) parts.push(`-v${p.serviceVkn}`);
    if (p.bankCode !== undefined) parts.push(`-c${p.bankCode}`);
    if (p.uid) parts.push(`-g${p.uid}`);
    if (!p.runWithoutDate) {
      const b = toCliDate(p.beginDate);
      const e = toCliDate(p.endDate);
      if (b) parts.push(`-b${b}`);
      if (e) parts.push(`-e${e}`);
    }
    if (p.inspect) parts.push('-i');
    if (p.dataOnly) parts.push('-d');
    if (p.skipJobLog) parts.push('-s');
    if (p.nextRunSkip) parts.push('-a');
    if (p.manuelRun) parts.push('-k 1');
    if (p.requestInclude) parts.push('-w***');
    return parts.join(' ');
  } catch {
    return requestParametersJson;
  }
};

const formPreviewBoxSx = {
  bgcolor: '#1e1e1e',
  color: '#d4d4d4',
  fontFamily: 'Consolas, "Courier New", monospace',
  fontSize: '0.75rem',
  p: 1.5,
  borderRadius: 1,
  overflow: 'auto',
  maxHeight: 140,
  whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-word' as const,
  border: '1px solid #333',
};

const CHECKBOX_OPTIONS: Array<{
  key: keyof typeof defaultFormState;
  label: string;
  tooltip: string;
}> = [
  { key: 'runWithoutDate', label: 'Tarih kullanma', tooltip: 'İşaretlenirse başlangıç/bitiş tarihi kullanılmaz; servis -n (nodate) ile çalışır.' },
  { key: 'dataOnly', label: 'Sadece veri', tooltip: 'Sadece veri çekilir; (-d).' },
  { key: 'inspect', label: 'Denetleme', tooltip: 'Denetleme (inspect) modunda çalıştırır (-i).' },
  { key: 'skipJobLog', label: 'İş günlüğünü atla', tooltip: 'İş günlüğü kaydı yazılmaz (-s).' },
  { key: 'nextRunSkip', label: 'Çalışma zamanını atla', tooltip: 'Bir sonraki çalışma zamanı kontrolü atlanır (-a).' },
  { key: 'manuelRun', label: 'Manuel çalıştırma', tooltip: 'Manuel tetikleme olarak işaretlenir (-k).' },
  { key: 'requestInclude', label: "Request'i dahil et", tooltip: "Aktif ise agent konfigürasyonundaki Run Service Admin Passwd -w parametresi ile gönderilir." },
];

export interface TetiklemeDialogInitialValues {
  serverId?: string;
  serviceVkn?: string;
  bankCode?: string;
  uid?: string;
  beginDate?: string;
  endDate?: string;
}

export interface TetiklemeDialogProps {
  open: boolean;
  onClose: () => void;
  initialValues?: TetiklemeDialogInitialValues;
  onSuccess?: () => void;
}

export const TetiklemeDialog: React.FC<TetiklemeDialogProps> = ({
  open,
  onClose,
  initialValues,
  onSuccess,
}) => {
  const [servers, setServers] = useState<Server[]>([]);
  const [bankalar, setBankalar] = useState<Banka[]>([]);
  const [sirketHesaplar, setSirketHesaplar] = useState<SirketHesapDto[]>([]);
  const [agents, setAgents] = useState<Array<{ serverId: string; isOnline: boolean }>>([]);
  const [form, setForm] = useState(defaultFormState);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const reachableServerIds = React.useMemo(
    () => new Set(agents.filter((a) => a.isOnline).map((a) => a.serverId)),
    [agents]
  );

  const loadServers = useCallback(async () => {
    try {
      const data = await serverService.getAll();
      setServers(data);
    } catch {
      setServers([]);
    }
  }, []);

  const loadAgents = useCallback(async () => {
    try {
      const data = await agentService.getAll();
      setAgents(data.map((a) => ({ serverId: a.serverId, isOnline: a.isOnline })));
    } catch {
      setAgents([]);
    }
  }, []);

  const loadBankalar = useCallback(async () => {
    try {
      const data = await bankaService.getAll();
      setBankalar(data);
    } catch {
      setBankalar([]);
    }
  }, []);

  const loadSirketHesaplar = useCallback(async (serverId: string, vkn?: string, bankaKodu?: number) => {
    if (!serverId) return;
    try {
      const data = await bankServiceRunService.getSirketHesaplar(
        serverId,
        vkn || undefined,
        bankaKodu
      );
      setSirketHesaplar(data);
    } catch {
      setSirketHesaplar([]);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadServers();
      loadAgents();
      loadBankalar();
    }
  }, [open, loadServers, loadAgents, loadBankalar]);

  useEffect(() => {
    if (!open) return;
    if (initialValues && Object.keys(initialValues).length > 0) {
      const now = getDateTimeLocalNow();
      setForm({
        ...defaultFormState,
        serverId: initialValues.serverId ?? '',
        serviceVkn: initialValues.serviceVkn ?? '',
        bankCode: initialValues.bankCode ?? '',
        uid: initialValues.uid ?? '',
        beginDate: initialValues.beginDate ?? now,
        endDate: initialValues.endDate ?? now,
        runWithoutDate: !initialValues.beginDate && !initialValues.endDate,
      });
    } else {
      setForm({
        ...defaultFormState,
        serverId: servers.length > 0 ? servers[0].id : '',
        beginDate: getDateTimeLocalNow(),
        endDate: getDateTimeLocalOneDayLater(),
      });
    }
    setFormError(null);
  }, [open, initialValues, servers.length]);

  useEffect(() => {
    if (form.serverId && (form.serviceVkn || form.bankCode !== '')) {
      loadSirketHesaplar(
        form.serverId,
        form.serviceVkn || undefined,
        form.bankCode !== '' ? Number(form.bankCode) : undefined
      );
    } else {
      setSirketHesaplar([]);
    }
  }, [form.serverId, form.serviceVkn, form.bankCode, loadSirketHesaplar]);

  const buildRequestParameters = (): string => {
    const params: RequestParams = {
      bankCode: form.bankCode !== '' ? Number(form.bankCode) : undefined,
      serviceVkn: form.serviceVkn || undefined,
      uid: form.uid || undefined,
      dataOnly: form.dataOnly,
      inspect: form.inspect,
      skipJobLog: form.skipJobLog,
      nextRunSkip: form.nextRunSkip,
      manuelRun: form.manuelRun,
      runWithoutDate: form.runWithoutDate,
      requestInclude: form.requestInclude,
    };
    if (!form.runWithoutDate) {
      if (form.beginDate) params.beginDate = form.beginDate;
      if (form.endDate) params.endDate = form.endDate;
    }
    return JSON.stringify(params);
  };

  const handleClose = () => {
    setForm(defaultFormState);
    setFormError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.serverId) {
      setFormError('Sunucu seçiniz.');
      return;
    }
    setSubmitLoading(true);
    setFormError(null);
    try {
      await bankServiceRunService.create({
        serverId: form.serverId,
        requestParameters: buildRequestParameters(),
        requestResponseDownload: form.requestResponseDownload,
      });
      handleClose();
      onSuccess?.();
    } catch (err: unknown) {
      const res = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { status?: number; data?: { message?: string } } }).response
        : null;
      const message = res?.data?.message ?? null;
      setFormError(message || 'İstek gönderilirken hata oluştu');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Yeni Tetikleme</DialogTitle>
      <DialogContent sx={{ pt: 3, pb: 1, '& .MuiAutocomplete-root': { minWidth: 0 } }}>
        {formError && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {formError}
          </Alert>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.5,
            mb: 1.5,
            mt: 0.5,
          }}
        >
          <Autocomplete
            size="small"
            options={servers}
            value={servers.find((s) => s.id === form.serverId) ?? null}
            getOptionLabel={(s) => s.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, newValue) =>
              setForm((prev) => ({
                ...prev,
                serverId: newValue?.id ?? '',
                serviceVkn: '',
                bankCode: '',
                uid: '',
              }))
            }
            renderInput={(params) => <TextField {...params} label="Sunucu" placeholder="Sunucu ara..." />}
            noOptionsText="Sunucu yok"
            sx={{ width: '100%' }}
          />
          <SirketLookup
            value={form.serviceVkn}
            onChange={(vkn) =>
              setForm((prev) => ({
                ...prev,
                serviceVkn: vkn,
                bankCode: '',
                uid: '',
              }))
            }
            serverId={form.serverId || undefined}
            label="Şirket (VKN)"
            placeholder={form.serverId ? 'Şirket ara...' : 'Önce sunucu seçiniz'}
            size="small"
            disabled={!form.serverId}
            sx={{ width: '100%' }}
          />
          <Autocomplete
            size="small"
            options={sirketHesaplar}
            value={sirketHesaplar.find((h) => h.uid === form.uid) ?? null}
            getOptionLabel={(h) => h.uid}
            isOptionEqualToValue={(a, b) => a.uid === b.uid}
            onChange={(_, newValue) => setForm((prev) => ({ ...prev, uid: newValue?.uid ?? '' }))}
            disabled={!form.serviceVkn || !form.bankCode}
            renderInput={(params) => (
              <TextField
                {...params}
                label="UID"
                placeholder={form.serviceVkn && form.bankCode ? 'UID ara...' : 'Önce şirket ve banka seçiniz'}
              />
            )}
            noOptionsText="Hesap yok"
            sx={{ width: '100%' }}
          />
          {(() => {
            const bankaKodlari = Array.from(new Set(sirketHesaplar.map((h) => h.bankaKodu)));
            const filtrelenmisBankalar = bankalar.filter((b) => bankaKodlari.includes(b.bankaKodu));
            return (
              <Autocomplete
                size="small"
                options={filtrelenmisBankalar}
                value={filtrelenmisBankalar.find((b) => String(b.bankaKodu) === form.bankCode) ?? null}
                getOptionLabel={(b) => `${b.bankaAdi} (${b.bankaKodu})`}
                isOptionEqualToValue={(a, b) => a.bankaKodu === b.bankaKodu}
                onChange={(_, newValue) =>
                  setForm((prev) => ({ ...prev, bankCode: newValue ? String(newValue.bankaKodu) : '', uid: '' }))
                }
                disabled={!form.serviceVkn}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Banka"
                    placeholder={form.serviceVkn ? 'Banka ara...' : 'Önce şirket seçiniz'}
                  />
                )}
                noOptionsText="Banka yok"
                sx={{ width: '100%' }}
              />
            );
          })()}
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={form.runWithoutDate}
              onChange={(e) => {
                const checked = e.target.checked;
                const nowStr = getDateTimeLocalNow();
                setForm((prev) => ({
                  ...prev,
                  runWithoutDate: checked,
                  ...(checked ? { beginDate: nowStr, endDate: nowStr } : {}),
                }));
              }}
            />
          }
          label={
            <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
              {CHECKBOX_OPTIONS.find((o) => o.key === 'runWithoutDate')?.label}
              <Tooltip title={CHECKBOX_OPTIONS.find((o) => o.key === 'runWithoutDate')?.tooltip}>
                <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }} aria-label="Açıklama">
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          }
          sx={{ mb: 1 }}
        />
        {!form.runWithoutDate && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <TextField
              size="small"
              label="Başlangıç"
              type="datetime-local"
              value={form.beginDate}
              onChange={(e) => setForm((prev) => ({ ...prev, beginDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              size="small"
              label="Bitiş"
              type="datetime-local"
              value={form.endDate}
              onChange={(e) => setForm((prev) => ({ ...prev, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 0.5,
            mb: 1,
          }}
        >
          {CHECKBOX_OPTIONS.filter((o) => o.key !== 'runWithoutDate').map((opt) => (
            <FormControlLabel
              key={opt.key}
              control={
                <Checkbox
                  size="small"
                  checked={Boolean(form[opt.key])}
                  onChange={(e) => setForm((prev) => ({ ...prev, [opt.key]: e.target.checked }))}
                />
              }
              label={
                <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
                  {opt.label}
                  <Tooltip title={opt.tooltip}>
                    <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }} aria-label="Açıklama">
                      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />
          ))}
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={form.requestResponseDownload}
              onChange={(e) => setForm((prev) => ({ ...prev, requestResponseDownload: e.target.checked }))}
            />
          }
          label={
            <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
              Request/Response indir
              <Tooltip title="İşlem tamamlandığında istek ve yanıt bir dosya olarak kaydedilir; İstek Detayı ekranından indirilebilir.">
                <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }} aria-label="Açıklama">
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          }
          sx={{ mb: 1.5 }}
        />
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
          Request önizlemesi
        </Typography>
        <Box sx={formPreviewBoxSx} component="pre">
          {buildRequestCommandLine(buildRequestParameters()) || '(Parametreleri doldurun)'}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>İptal</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitLoading || !form.serverId || !reachableServerIds.has(form.serverId)}
        >
          {submitLoading ? 'Gönderiliyor...' : 'Tetikle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
